import { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { NotificationTextMessage } from '@/core/enums/notification';
import type { SelectedClient } from '@/core/models/practice.models';
import { TB_DRAFT_STATE, type TrialBalanceRow } from '@/core/models/trial-balance.models';
import { notifyError, notifySuccess } from '@/core/notify/notify';
import { matrixPath } from '@/core/routing/matrix';
import { selectedClient } from '@/core/selectedClient';
import { trialBalanceService } from '@/core/services/trialBalance';
import { IMPORT_EXCEPTIONS_ROUTE } from '@/features/taxreturn/menus/menuItems';
import { SideMenuPage } from '@/features/taxreturn/menus/SideMenuPage';
import { Button } from '@/kit/Button';
import { Checkbox } from '@/kit/Checkbox';
import { ClosePopup } from '@/kit/ClosePopup';
import { SelectionBar } from '@/kit/SelectionBar';
import { DataTable, type DataTableColumn } from '@/kit/DataTable';
import { EmptyState } from '@/kit/EmptyState';
import { Icon } from '@/kit/Icon';
import { openDialog } from '@/kit/dialog';
import { TrialBalanceDialog, type TrialBalanceDialogData } from './TrialBalanceDialog';
import { tbViewPath } from './trialBalanceEditor';
import './TaskTablePage.scss';

const editPath = (id: string) => matrixPath('/home/taxreturns', 'tasks-createtrialbalance', { id: btoa(id) });
const NEW_TB_PATH = '/home/taxreturns/tasks-createtrialbalance';

/** Angular TaxreturnTasksComponent: the client's trial balances (list, edit, delete, export, add). */
export function TaxreturnTasks() {
  const navigate = useNavigate();
  // APT378: the client dashboard's out of balance tile opens this list with ?filter=unbalanced.
  const [searchParams, setSearchParams] = useSearchParams();
  const unbalancedOnly = searchParams.get('filter') === 'unbalanced';
  const [dataSource, setDataSource] = useState<TrialBalanceRow[]>([]);
  /** The first load has finished: only then is an empty list "empty" (not still loading). */
  const [loaded, setLoaded] = useState(false);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const clientRef = useRef<SelectedClient | null>(null);

  const load = () => {
    const client = clientRef.current;
    if (!client) {
      return;
    }
    trialBalanceService.getTrialBalances(client.id).then(
      (rows) => {
        setDataSource(rows);
        const ids = new Set(rows.map((r) => r.id));
        setSelectedIds((current) => new Set([...current].filter((id) => ids.has(id))));
        setLoaded(true);
      },
      (error) => {
        setLoaded(true);
        notifyError(error);
      },
    );
  };

  useEffect(() => {
    clientRef.current = selectedClient();
    if (!clientRef.current) {
      notifyError('Select a client from Accounts first.');
      navigate('/home/accountclient');
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- ngOnInit: once
  }, []);

  const visibleRows = unbalancedOnly ? dataSource.filter((r) => !r.isBalanced) : dataSource;
  const isAllSelected = visibleRows.length > 0 && visibleRows.every((r) => selectedIds.has(r.id));
  const isSomeSelected = selectedIds.size > 0 && !isAllSelected;

  const toggleAll = (checked: boolean) => setSelectedIds(checked ? new Set(visibleRows.map((r) => r.id)) : new Set());

  const toggleRow = (row: TrialBalanceRow, checked: boolean) =>
    setSelectedIds((current) => {
      const next = new Set(current);
      if (checked) {
        next.add(row.id);
      } else {
        next.delete(row.id);
      }
      return next;
    });

  const onEditClick = (row: TrialBalanceRow) => navigate(editPath(row.id));
  /** APT366: the reference number opens the trial balance as a read-only statement; the pencil still opens the editor. */
  const onViewClick = (row: TrialBalanceRow) => navigate(tbViewPath(row.id));

  const openDeleteClick = (row: TrialBalanceRow) =>
    // The ticked rows when this row is one of them, so the checkboxes drive the action.
    confirmDelete(selectedIds.has(row.id) && selectedIds.size > 1 ? [...selectedIds] : [row.id]);

  const confirmDelete = (ids: string[]) => {
    openDialog(ClosePopup, {
      data: { itemName: ids.length > 1 ? 'Trial Balances' : 'Trial Balance', count: ids.length },
    })
      .afterClosed()
      .then((confirmed) => {
        if (!confirmed) {
          return;
        }
        trialBalanceService.deleteTrialBalances(ids).then(
          () => {
            setSelectedIds((current) => {
              const next = new Set(current);
              ids.forEach((id) => next.delete(id));
              return next;
            });
            notifySuccess(NotificationTextMessage.recordDeletedSuccessfully);
            load();
          },
          (error) => notifyError(error),
        );
      });
  };

  /** format: 1 CSV, 3 PDF. */
  const onExport = (row: TrialBalanceRow, format: number) => {
    trialBalanceService.exportTrialBalance(row.id, format).catch((error) => notifyError(error));
  };

  const openTrialBalanceDialog = () => {
    const client = clientRef.current;
    if (!client) {
      return;
    }
    const data: TrialBalanceDialogData = { clientId: client.id };
    // BUG-037: Manual saves nothing; the editor's Save creates the trial balance (BUG-036: a file's lines travel with the
    // draft, unsaved). BUG-038: a Bookkeeping import created it already, so it opens like any saved trial balance.
    openDialog(TrialBalanceDialog, { disableClose: true, panelClass: 'cst-invite', data })
      .afterClosed()
      .then((result) => {
        if (result && 'created' in result) {
          navigate(editPath(result.created.id));
        } else if (result) {
          navigate(NEW_TB_PATH, { state: { [TB_DRAFT_STATE]: result.draft } });
        }
      });
  };

  const columns: DataTableColumn<TrialBalanceRow>[] = [
    {
      id: 'no',
      header: (
        <>
          <Checkbox
            checked={isAllSelected}
            indeterminate={isSomeSelected}
            onChange={(event) => toggleAll(event.target.checked)}
          />
          Ref. No.
        </>
      ),
      cell: (element) => (
        <>
          <Checkbox
            checked={selectedIds.has(element.id)}
            onChange={(event) => toggleRow(element, event.target.checked)}
          />
          <a className="link" title="View this trial balance" onClick={() => onViewClick(element)}>
            {element.refNo}
          </a>
        </>
      ),
    },
    { id: 'from', header: 'From', cell: (element) => element.fromDate },
    { id: 'to', header: 'To', cell: (element) => element.toDate },
    { id: 'description', header: 'Description', cell: (element) => element.description },
    { id: 'import', header: 'Mode of Import', cell: (element) => element.importMode },
    { id: 'type', header: 'Type', cell: (element) => element.type },
    {
      id: 'status',
      header: 'Status',
      cell: (element) => (
        <div className={`d-flex align-items-center gap-5 ${element.isBalanced ? 'text-green' : 'text-red'}`}>
          <Icon name={element.isBalanced ? 'check_circle' : 'error'} />
          <span>{element.status}</span>
        </div>
      ),
    },
    {
      id: 'actions',
      header: 'Action',
      cell: (element) => <RowActions element={element} onEdit={onEditClick} onDelete={openDeleteClick} onExport={onExport} />,
    },
  ];

  return (
    <SideMenuPage menu="task" contentClassName="tr-table-page">
      <div className="content-body">
        <div className="content-header mb-0">
          <div className="content-header-left mb-1">
            <Button className="action-button primary-button" onClick={openTrialBalanceDialog}>
              <Icon name="add" />
              <span>Add Trial Balance</span>
            </Button>
            {/* APT346 (client): Import Exceptions moved here from the side menu, "to keep all Trial Balance-related activities in one central location". */}
            <Button className="action-button secondary-button ml-1" onClick={() => void navigate(IMPORT_EXCEPTIONS_ROUTE)}>
              <Icon name="rule" />
              <span>Import Exceptions</span>
            </Button>
          </div>
        </div>

        <SelectionBar
          count={selectedIds.size}
          onDelete={() => confirmDelete([...selectedIds])}
          onClear={() => setSelectedIds(new Set())}
        />
        {unbalancedOnly && (
          <div className="d-flex align-items-center gap-10 mb-1" data-testid="unbalanced-filter">
            <span>Showing only the trial balances that are out of balance.</span>
            <Button className="action-button secondary-button" onClick={() => setSearchParams({}, { replace: true })}>
              <span>Show all</span>
            </Button>
          </div>
        )}
        <div className="mat-table-wrapper">
          <DataTable
            columns={columns}
            rows={visibleRows}
            rowKey={(row) => row.id}
            stickyHeader
            noDataMessage={
              loaded && unbalancedOnly && dataSource.length > 0 ? (
                <EmptyState icon="check_circle" title="Every trial balance balances." />
              ) : loaded ? (
                <EmptyState
                  icon="description"
                  title="No trial balances yet"
                  hint="Add a trial balance to start preparing this client's accounts."
                  action={
                    <Button className="action-button primary-button" onClick={openTrialBalanceDialog}>
                      <Icon name="add" />
                      <span>Add first trial balance</span>
                    </Button>
                  }
                />
              ) : undefined
            }
          />
        </div>
      </div>
    </SideMenuPage>
  );
}

interface RowActionsProps {
  element: TrialBalanceRow;
  onEdit: (row: TrialBalanceRow) => void;
  onDelete: (row: TrialBalanceRow) => void;
  onExport: (row: TrialBalanceRow, format: number) => void;
}

function RowActions({ element, onEdit, onDelete, onExport }: RowActionsProps) {
  return (
    <div className="d-flex align-items-center grid-actions">
      <Button title="Edit" onClick={() => onEdit(element)}>
        <Icon name="edit" outlined />
      </Button>
      <Button title="Delete" onClick={() => onDelete(element)}>
        <Icon name="delete_outline" outlined />
      </Button>
      <Button title="Download as PDF" onClick={() => onExport(element, 3)}>
        <Icon name="picture_as_pdf" outlined className="icon-pdf" />
      </Button>
      <Button title="Download as CSV" onClick={() => onExport(element, 1)}>
        <Icon name="description" outlined className="icon-csv" />
      </Button>
    </div>
  );
}
